/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

/**
 * Helpers for the Vega OS (Amazon Fire TV) WebView host.
 *
 * The Vega RN shell exposes `window.ReactNativeWebView.postMessage(string)` as the
 * web-to-native bridge. Messages are received by the shell's `onMessage` prop and
 * routed to the appropriate native action.
 *
 * Mirrors the shape of `tizen.ts` to keep platform-specific runtime checks consistent.
 */

interface ReactNativeWebViewBridge {
    postMessage(message: string): void;
}

interface WindowWithVegaBridge extends Window {
    ReactNativeWebView?: ReactNativeWebViewBridge;
}

/**
 * Plain-string messages understood by the Vega RN shell's `onMessage` handler.
 */
export enum VegaShellMessage {
    ExitApp = 'EXIT_APP'
}

/** Checks if the page is hosted inside the Vega RN WebView. */
export function isVegaWebViewAvailable(): boolean {
    return typeof (window as WindowWithVegaBridge).ReactNativeWebView?.postMessage === 'function';
}

/** Sends a message to the Vega RN shell via the WebView postMessage bridge. */
export function postMessageToVegaShell(message: VegaShellMessage): void {
    (window as WindowWithVegaBridge).ReactNativeWebView?.postMessage(message);
}

/** Requests the Vega RN shell to exit the application. */
export function exitVegaApplication(): void {
    postMessageToVegaShell(VegaShellMessage.ExitApp);
}
